import { Pane } from 'tweakpane';
import * as THREE from 'three';
import { params, clampPowerOfTwo } from '../config.js';
import * as SceneModule from '../scene.js';
import { applyYawPitchDeg, applyFogFromParams, getDefaultCamPos, getDefaultCamQuat } from '../scene.js';
import { airplaneState } from '../airplane/state.js';
import { getAirplaneGroup } from '../airplane/create.js';
import { pointMaterial } from '../terrain/material.js';
import { state, scrollOffset, rebuildTiling, updateTileMatrices } from '../terrain/tiling.js';
import { setCameraMode } from '../camera/modes.js';
import { cameraOrbit } from '../controls/mouse.js';
import { bloomConfig, updateBloomConfig } from '../effects/composer.js';

// Tweakpane instance
export let pane = null;

// Panel visibility state
let panelVisible = false;

// DOM elements
let panelContainer = null;
let hudControls = null;
let devPanelToggle = null;
let fileInput = null;

export function updatePanelVisibility() {
  if (panelVisible) {
    if (panelContainer) panelContainer.classList.remove('hidden');
    if (devPanelToggle) devPanelToggle.classList.add('panel-open');
  } else {
    if (panelContainer) panelContainer.classList.add('hidden');
    if (devPanelToggle) devPanelToggle.classList.remove('panel-open');
  }
  // Remove panel-hidden from HUD controls on initialization (HUD visibility is controlled by throttle, not panel)
  if (hudControls) {
    hudControls.classList.remove('panel-hidden');
  }
}

export function togglePanel() {
  panelVisible = !panelVisible;
  updatePanelVisibility();
}

export function setupTweakpane() {
  // Get DOM elements
  panelContainer = document.getElementById('panel-container');
  hudControls = document.getElementById('hud-controls');
  devPanelToggle = document.getElementById('dev-panel-toggle');
  fileInput = document.getElementById('heightmapFile');
  
  // Remove panel-hidden from HUD controls so they can be visible (visibility controlled by throttle, not panel)
  // This must happen before updatePanelVisibility() is called
  if (hudControls) {
    hudControls.classList.remove('panel-hidden');
  }

  // Create Tweakpane
  pane = new Pane({ title: 'Particle Landscape', container: panelContainer });

  // Dev Panel Toggle
  if (devPanelToggle) {
    devPanelToggle.addEventListener('click', (e) => {
      e.preventDefault();
      togglePanel();
    });
  }

  // Initialize panel as hidden (this no longer affects HUD controls)
  updatePanelVisibility();

  // Heightmap folder
  const heightmapFolder = pane.addFolder({ title: 'Heightmap', expanded: true });
  heightmapFolder.addButton({ title: 'Load Heightmap...' }).on('click', () => {
    fileInput.click();
  });
  heightmapFolder.addBinding(params, 'terrainColor', { view: 'color' }).on('change', (ev) => {
    pointMaterial.uniforms.uColor.value.set(ev.value);
  });
  heightmapFolder.addBinding(params, 'scale', { min: 0, max: 24, step: 0.1 }).on('change', (ev) => {
    pointMaterial.uniforms.uScale.value = ev.value;
    pointMaterial.uniforms.uBias.value = -ev.value * 0.5;
  });
  heightmapFolder.addBinding(params, 'tileSize', { min: 2, max: 150, step: 1 }).on('change', (ev) => {
    state.tileSize = Math.max(1, ev.value);
    scrollOffset.set(0, 0);
    rebuildTiling();
  });
  heightmapFolder.addBinding(params, 'tilesX', { min: 1, max: 20, step: 1 }).on('change', (ev) => {
    state.tilesX = Math.max(1, Math.min(20, ev.value));
    scrollOffset.set(0, 0);
    rebuildTiling();
  });
  heightmapFolder.addBinding(params, 'tilesZ', { min: 1, max: 20, step: 1 }).on('change', (ev) => {
    state.tilesZ = Math.max(1, Math.min(20, ev.value));
    scrollOffset.set(0, 0);
    rebuildTiling();
  });

  // Particles folder
  const particlesFolder = pane.addFolder({ title: 'Particles', expanded: true });
  particlesFolder.addBinding(params, 'pointSize', { min: 0, max: 0.11, step: 0.01, label: 'Point Size' }).on('change', (ev) => {
    pointMaterial.uniforms.uPointSize.value = ev.value;
  });
  particlesFolder.addBinding(params, 'jitter', { min: 0.0, max: 0.2, step: 0.001 }).on('change', (ev) => {
    pointMaterial.uniforms.uJitter.value = ev.value;
  });
  particlesFolder.addBlade({ view: 'separator' });
  particlesFolder.addBinding(params, 'hoverRadius', { min: 0.1, max: 10, step: 0.1, label: 'Hover Radius' }).on('change', (ev) => {
    pointMaterial.uniforms.uHoverRadius.value = ev.value;
  });
  particlesFolder.addBinding(params, 'hoverStrength', { min: 0, max: 10, step: 0.1, label: 'Hover Strength' }).on('change', (ev) => {
    pointMaterial.uniforms.uHoverStrength.value = ev.value;
  });
  particlesFolder.addBinding(params, 'hoverSnap', { min: 0.1, max: 10, step: 0.1, label: 'Hover Snap' }).on('change', (ev) => {
    pointMaterial.uniforms.uHoverSnap.value = ev.value;
  });
  particlesFolder.addBinding(params, 'hoverColor', { view: 'color', label: 'Hover Color' }).on('change', (ev) => {
    pointMaterial.uniforms.uHoverColor.value.set(ev.value);
  });
  particlesFolder.addBinding(params, 'mouseHoverEnabled', { label: 'Mouse Hover' }).on('change', (ev) => {
    pointMaterial.uniforms.uHoverEnabled.value = ev.value ? 1.0 : 0.0;
  });
  particlesFolder.addBlade({ view: 'separator' });
  particlesFolder.addBinding(params, 'cols', { min: 8, max: 1024, step: 8 }).on('change', (ev) => {
    params.cols = clampPowerOfTwo(ev.value);
    rebuildTiling();
  });
  particlesFolder.addBinding(params, 'rows', { min: 8, max: 1024, step: 8 }).on('change', (ev) => {
    params.rows = clampPowerOfTwo(ev.value);
    rebuildTiling();
  });

  // Mask folder
  const maskFolder = pane.addFolder({ title: 'Mask', expanded: true });
  maskFolder.addBinding(params, 'maskRadius', { min: 0.1, max: 20, step: 0.1, label: 'Mask Radius' }).on('change', (ev) => {
    pointMaterial.uniforms.uMaskRadius.value = ev.value;
  });
  maskFolder.addBinding(params, 'maskFeather', { min: 0.0, max: 5, step: 0.1, label: 'Mask Feather' }).on('change', (ev) => {
    pointMaterial.uniforms.uMaskFeather.value = ev.value;
  });

  // Animation folder
  const animationFolder = pane.addFolder({ title: 'Animation', expanded: true });
  // scrollSpeed is unified - state.scrollSpeed is a getter/setter for params.scrollSpeed
  animationFolder.addBinding(params, 'scrollSpeed', { min: -20, max: 20, step: 0.1, label: 'Scroll Speed' });
  animationFolder.addBinding(params, 'animateScroll', { label: 'Animate' });

  // Airplane folder
  const airplaneFolder = pane.addFolder({ title: 'Airplane', expanded: true });
  airplaneFolder.addBinding(params, 'airplaneEnabled', { label: 'Enable Airplane' }).on('change', (ev) => {
    airplaneState.enabled = ev.value;
    const airplaneGroup = getAirplaneGroup();
    if (airplaneGroup) airplaneGroup.visible = ev.value;
    if (ev.value) {
      airplaneState.heading = 0;
      airplaneState.targetHeading = 0;
      airplaneState.y = 0;
      airplaneState.targetY = 0;
      airplaneState.tilt = 0;
      airplaneState.pitch = 0;
    }
  });
  airplaneFolder.addBinding(params, 'airplaneSpeed', { min: 1, max: 20, step: 0.5, label: 'Move Speed' }).on('change', (ev) => {
    airplaneState.speed = ev.value;
  });
  airplaneFolder.addBinding(params, 'airplaneMaxOffset', { min: 1, max: 15, step: 0.5, label: 'Max Offset' }).on('change', (ev) => {
    airplaneState.maxOffset = ev.value;
  });
  airplaneFolder.addBinding(params, 'airplaneVertSpeed', { min: 0.5, max: 5, step: 0.1, label: 'Vert Speed' }).on('change', (ev) => {
    airplaneState.vertSpeed = ev.value;
  });
  airplaneFolder.addBinding(params, 'airplaneAltitudeRange', { min: 0.5, max: 5, step: 0.1, label: 'Alt Range' }).on('change', (ev) => {
    airplaneState.altitudeRange = ev.value;
  });
  airplaneFolder.addBlade({ view: 'separator' });
  airplaneFolder.addBinding(params, 'strafeSpeed', { min: 0.1, max: 5, step: 0.1, label: 'Strafe Speed' }).on('change', (ev) => {
    airplaneState.strafeSpeed = ev.value;
  });
  airplaneFolder.addBinding(params, 'strafeEasing', { min: 0.5, max: 10, step: 0.5, label: 'Strafe Easing' }).on('change', (ev) => {
    airplaneState.strafeEasing = ev.value;
  });
  airplaneFolder.addBinding(params, 'maxStrafe', { min: 1, max: 15, step: 0.5, label: 'Max Strafe' }).on('change', (ev) => {
    airplaneState.maxStrafe = ev.value;
  });
  airplaneFolder.addBlade({ view: 'separator' });
  airplaneFolder.addBinding(params, 'airplaneHeight', { min: -5, max: 1, step: 0.1, label: 'Height' }).on('change', (ev) => {
    airplaneState.height = ev.value;
  });
  airplaneFolder.addBinding(params, 'airplaneSize', { min: 0.2, max: 3, step: 0.1, label: 'Size' }).on('change', (ev) => {
    airplaneState.size = ev.value;
  });
  airplaneFolder.addBinding(params, 'airplaneDistance', { min: 1, max: 10, step: 0.1, label: 'Distance' }).on('change', (ev) => {
    airplaneState.distance = ev.value;
  });
  airplaneFolder.addBlade({ view: 'separator' });
  airplaneFolder.addBinding(params, 'airplaneHoverEnabled', { label: 'Ground Highlight' }).on('change', (ev) => {
    pointMaterial.uniforms.uAirplaneHoverEnabled.value = ev.value ? 1.0 : 0.0;
  });
  airplaneFolder.addBinding(params, 'airplaneHoverRadius', { min: 0.5, max: 5, step: 0.1, label: 'Highlight Size' }).on('change', (ev) => {
    pointMaterial.uniforms.uAirplaneHoverRadius.value = ev.value;
  });
  airplaneFolder.addBinding(params, 'airplaneColorSafe', { view: 'color', label: 'Safe Color' }).on('change', (ev) => {
    pointMaterial.uniforms.uAirplaneColorFar.value.set(ev.value);
  });
  airplaneFolder.addBinding(params, 'airplaneColorDanger', { view: 'color', label: 'Danger Color' }).on('change', (ev) => {
    pointMaterial.uniforms.uAirplaneColorClose.value.set(ev.value);
  });
  airplaneFolder.addBlade({ view: 'separator' });
  const airplaneHint = airplaneFolder.addBlade({
    view: 'text',
    label: '',
    parse: (v) => String(v),
    value: 'WS=Throttle, AD=Strafe',
  });
  airplaneHint.disabled = true;

  // Dust Wake folder - particles react to low-flying fast airplane
  const dustWakeFolder = pane.addFolder({ title: 'Dust Wake Effect', expanded: false });
  dustWakeFolder.addBinding(params, 'dustWakeEnabled', { label: 'Enable Dust Wake' }).on('change', (ev) => {
    pointMaterial.uniforms.uDustWakeEnabled.value = ev.value ? 1.0 : 0.0;
  });
  dustWakeFolder.addBinding(params, 'dustWakeRadius', { min: 0.5, max: 5.0, step: 0.1, label: 'Effect Radius' }).on('change', (ev) => {
    pointMaterial.uniforms.uDustWakeRadius.value = ev.value;
  });
  dustWakeFolder.addBinding(params, 'dustWakeHeight', { min: 0.05, max: 0.5, step: 0.02, label: 'Depress Depth' }).on('change', (ev) => {
    pointMaterial.uniforms.uDustWakeHeight.value = ev.value;
  });
  dustWakeFolder.addBinding(params, 'dustWakeSpeedThreshold', { min: 1.0, max: 10.0, step: 0.5, label: 'Min Speed' }).on('change', (ev) => {
    pointMaterial.uniforms.uDustWakeSpeedThreshold.value = ev.value;
  });
  dustWakeFolder.addBinding(params, 'dustWakeClearanceMax', { min: 0.3, max: 3.0, step: 0.1, label: 'Max Altitude' }).on('change', (ev) => {
    pointMaterial.uniforms.uDustWakeClearanceMax.value = ev.value;
  });
  dustWakeFolder.addBinding(params, 'dustWakeTrailLength', { min: 1.0, max: 40.0, step: 1.0, label: 'Trail Length' }).on('change', (ev) => {
    pointMaterial.uniforms.uDustWakeTrailLength.value = ev.value;
  });
  dustWakeFolder.addBinding(params, 'dustWakeColor', { view: 'color', label: 'Dust Color' }).on('change', (ev) => {
    pointMaterial.uniforms.uDustWakeColor.value.set(ev.value);
  });


  // Bloom folder
  const bloomFolder = pane.addFolder({ title: 'Bloom Effect', expanded: true });
  bloomFolder.addBinding(bloomConfig, 'enabled', { label: 'Enable Bloom' });
  bloomFolder.addBinding(bloomConfig, 'strength', { min: 0.0, max: 3.0, step: 0.1, label: 'Strength' }).on('change', () => {
    updateBloomConfig();
  });
  bloomFolder.addBinding(bloomConfig, 'radius', { min: 0.0, max: 1.0, step: 0.01, label: 'Radius' }).on('change', () => {
    updateBloomConfig();
  });
  bloomFolder.addBinding(bloomConfig, 'threshold', { min: 0.0, max: 1.0, step: 0.01, label: 'Threshold' }).on('change', () => {
    updateBloomConfig();
  });

  // Camera folder
  const cameraFolder = pane.addFolder({ title: 'Camera', expanded: true });
  cameraFolder.addBinding(params, 'camYaw', { min: -180, max: 180, step: 1, label: 'Yaw' }).on('change', (ev) => {
    applyYawPitchDeg(ev.value, params.camPitch);
  });
  cameraFolder.addBinding(params, 'camPitch', { min: -89, max: 89, step: 1, label: 'Pitch' }).on('change', (ev) => {
    applyYawPitchDeg(params.camYaw, ev.value);
  });
  cameraFolder.addBinding(params, 'camFov', { min: 20, max: 120, step: 1, label: 'FOV' }).on('change', (ev) => {
    const { camera } = SceneModule;
    camera.fov = ev.value;
    camera.updateProjectionMatrix();
  });
  cameraFolder.addBinding(params, 'minFov', { min: 50, max: 100, step: 1, label: 'Min FOV (slow)' });
  cameraFolder.addBinding(params, 'maxFov', { min: 70, max: 120, step: 1, label: 'Max FOV (fast)' });
  cameraFolder.addBinding(params, 'fovEasing', { min: 0.3, max: 5.0, step: 0.1, label: 'FOV Smoothing' });
  cameraFolder.addBinding(params, 'camHeight', { min: 0, max: 100, step: 0.1, label: 'Height' }).on('change', (ev) => {
    const { camera } = SceneModule;
    camera.position.y = ev.value;
  });
  cameraFolder.addBinding(params, 'camZ', { min: -200, max: 200, step: 0.1, label: 'Camera Z' }).on('change', (ev) => {
    const { camera } = SceneModule;
    camera.position.z = ev.value;
  });
  cameraFolder.addBlade({ view: 'separator' });
  cameraFolder.addBinding(params, 'fogEnabled', { label: 'Fog Enabled' }).on('change', applyFogFromParams);
  cameraFolder.addBinding(params, 'fogColor', { view: 'color', label: 'Fog Color' }).on('change', applyFogFromParams);
  cameraFolder.addBinding(params, 'fogStart', { min: 0, max: 50, step: 1, label: 'Fog Start' }).on('change', applyFogFromParams);
  cameraFolder.addBinding(params, 'fogEnd', { min: 1, max: 50, step: 1, label: 'Fog End' }).on('change', applyFogFromParams);
  cameraFolder.addBlade({ view: 'separator' });
  cameraFolder.addButton({ title: 'Reset Camera' }).on('click', () => {
    const { camera } = SceneModule;
    const defaultCamPos = getDefaultCamPos();
    const defaultCamQuat = getDefaultCamQuat();
    if (defaultCamPos && defaultCamQuat) {
      camera.position.copy(defaultCamPos);
      camera.quaternion.copy(defaultCamQuat);
    }
    const e = new THREE.Euler().setFromQuaternion(camera.quaternion, 'YXZ');
    params.camYaw = Math.round(THREE.MathUtils.radToDeg(e.y));
    params.camPitch = Math.round(THREE.MathUtils.radToDeg(e.x));
    params.camFov = Math.round(camera.fov);
    params.camHeight = camera.position.y;
    params.camZ = camera.position.z;
    params.cameraMode = 'chase';
    cameraOrbit.azimuth = 0;
    cameraOrbit.elevation = 0;
    pane.refresh();
    scrollOffset.set(0, 0);
    updateTileMatrices();
  });
  cameraFolder.addBlade({ view: 'separator' });
  cameraFolder.addBinding(params, 'cameraMode', {
    label: 'Camera Mode (G)',
    options: { 'Chase': 'chase', 'Top-Down': 'topdown' }
  }).on('change', (ev) => {
    setCameraMode(ev.value);
  });
  cameraFolder.addBinding(params, 'chaseCamDistance', { min: 0.5, max: 8, step: 0.1, label: 'Chase Distance' });
  cameraFolder.addBinding(params, 'chaseCamHeight', { min: 0.2, max: 3, step: 0.1, label: 'Chase Height' });
  cameraFolder.addBinding(params, 'topCamHeight', { min: 3, max: 20, step: 0.5, label: 'Top-Down Height' });
  cameraFolder.addBinding(params, 'chaseCamAltitude', { min: 0.5, max: 5, step: 0.1, label: 'Flight Altitude' }).on('change', (ev) => {
    airplaneState.baseAltitude = ev.value;
  });

  // Dev Tools folder
  const devToolsFolder = pane.addFolder({ title: 'Dev Tools', expanded: false });
  const copyParamsBtn = devToolsFolder.addButton({ title: '📋 Copy Params to Clipboard' });
  copyParamsBtn.on('click', () => {
    const formatValue = (v) => {
      if (typeof v === 'string') return `'${v}'`;
      if (typeof v === 'boolean') return v ? 'true' : 'false';
      return v;
    };

    const lines = ['    const params = {'];
    const groups = {
      'Heightmap': ['terrainColor', 'scale', 'tileSize', 'tilesX', 'tilesZ'],
      'Particles': ['pointSize', 'jitter', 'hoverRadius', 'hoverStrength', 'hoverSnap', 'hoverColor', 'cols', 'rows'],
      'Mask': ['maskRadius', 'maskFeather'],
      'Animation': ['scrollSpeed', 'animateScroll'],
      'Camera': ['camYaw', 'camPitch', 'camFov', 'camHeight', 'camZ'],
      'Fog': ['fogEnabled', 'fogColor', 'fogStart', 'fogEnd'],
      'Camera modes': ['cameraMode', 'chaseCamDistance', 'chaseCamHeight', 'chaseCamAltitude', 'topCamHeight'],
      'Airplane': ['airplaneEnabled', 'airplaneSpeed', 'airplaneMaxOffset', 'airplaneVertSpeed', 'airplaneAltitudeRange', 'airplaneHeight', 'airplaneSize', 'airplaneDistance'],
      'Hover highlights': ['mouseHoverEnabled', 'airplaneHoverEnabled', 'airplaneHoverRadius', 'airplaneColorSafe', 'airplaneColorDanger'],
      'Strafe settings': ['strafeSpeed', 'strafeEasing', 'maxStrafe'],
    };

    Object.entries(groups).forEach(([groupName, keys], groupIdx, groupArr) => {
      lines.push(`      // ${groupName}`);
      keys.forEach((key, keyIdx, keyArr) => {
        if (params.hasOwnProperty(key)) {
          const isLastInGroup = keyIdx === keyArr.length - 1;
          const isLastGroup = groupIdx === groupArr.length - 1;
          const comma = (isLastInGroup && isLastGroup) ? '' : ',';
          lines.push(`      ${key}: ${formatValue(params[key])}${comma}`);
        }
      });
    });
    lines.push('    };');

    const output = lines.join('\n');
    navigator.clipboard.writeText(output).then(() => {
      copyParamsBtn.title = '✅ Copied!';
      setTimeout(() => { copyParamsBtn.title = '📋 Copy Params to Clipboard'; }, 1500);
    }).catch(err => {
      console.error('Failed to copy:', err);
      alert('Failed to copy to clipboard. Check console for the params.');
      console.log(output);
    });
  });

  // Apply URL parameters
  applyUrlParams();

  return pane;
}

function applyUrlParams() {
  const urlParams = new URLSearchParams(window.location.search);

  function setParam(key, paramName) {
    if (urlParams.has(paramName)) {
      const value = urlParams.get(paramName);
      if (typeof params[key] === 'boolean') {
        params[key] = value === '1' || value === 'true' || value === 'on';
      } else {
        params[key] = Number(value) || value;
      }
    }
  }

  // hide UI if ui=0
  if (urlParams.get('ui') === '0' && pane) {
    pane.hidden = true;
  }

  // common controls
  setParam('scale', 'scale');
  setParam('tileSize', 'tileSize');
  setParam('tilesX', 'tilesX');
  setParam('tilesZ', 'tilesZ');
  setParam('pointSize', 'psize');
  setParam('maskRadius', 'mask');
  setParam('maskFeather', 'feather');
  setParam('jitter', 'jitter');
  setParam('hoverRadius', 'hR');
  setParam('hoverStrength', 'hS');
  setParam('hoverSnap', 'hSnap');
  setParam('camYaw', 'yaw');
  setParam('camPitch', 'pitch');
  setParam('camFov', 'fov');
  setParam('camHeight', 'height');
  setParam('camZ', 'z');
  setParam('scrollSpeed', 'scroll');
  setParam('animateScroll', 'animate');
  setParam('cols', 'cols');
  setParam('rows', 'rows');
  setParam('fogEnabled', 'fog');
  setParam('fogStart', 'fogStart');
  setParam('fogEnd', 'fogEnd');

  // colors as hex
  if (urlParams.has('terrain')) {
    params.terrainColor = urlParams.get('terrain');
  }
  if (urlParams.has('fogColor')) {
    params.fogColor = urlParams.get('fogColor');
  }

  // Refresh pane to show URL param values
  if (pane) pane.refresh();
}
